import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import RadioButtons from 'components/inputs/radio-buttons/RadioButtons';
import { ModalFrameFooter, ModalFrameInner } from 'components/modal';
import InfoNote from 'components/info-note/InfoNote';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import ActionLink from 'components/buttons/action-link/ActionLink';
import SearchableDropdown from 'components/inputs/searchable-dropdown/SearchableDropdown';

import { GENDER_OPTIONS, INFLUENCER_CHANNELS, getFullChannelUrl } from 'utils/influencers';
import { snakeCaseKeys } from 'utils/keys';
import { __t, applyTranslations } from 'utils/language';
import { isChannelUrl, isRequired } from 'utils/validators';
import { countrySearchToDropdownItems } from 'utils/locations';
import { getSelectedChannels, isChannelChecked } from 'modules/discovery/creators/utils/creators';

import type {
  DiscoveryCreatorsFilters,
  InfluencerProfile,
  InfluencerProfileFormLightweight,
  MixedSelectDropdownItem,
  TextEntry
} from 'types';

import type { ChangeEvent, SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./lightweight-profile-modal.json'));

type StateProps = {
  filters: DiscoveryCreatorsFilters,
  errorMessage?: string | null
};

type OwnProps = {
  onCancel: () => void,
  onSubmit: (a: InfluencerProfileFormLightweight) => Promise<InfluencerProfile | undefined | null>,
  instagramChannel?: string
};

type Props = StateProps & OwnProps;

type State = {
  email: string,
  channel: string,
  gender: string,
  channelType: string,
  isValid: boolean,
  isModified: boolean,
  existingProfileId: number | undefined | null,
  countries: Array<MixedSelectDropdownItem>
};

const COMMON_MSG_KEY = 'collabary_common';

class LightweightProfileForm extends React.Component<Props, State> {
  state: State = {
    email: '',
    channel: '',
    channelType: '',
    gender: '',
    isValid: true,
    isModified: false,
    countries: [],
    existingProfileId: null
  };

  constructor(props: Props) {
    super(props);

    const selectedChannels = getSelectedChannels(props.filters.channels);
    const channelType = (isChannelChecked(selectedChannels, 'youtube') ? 'youtube' : 'instagram');

    this.state.channel = props.instagramChannel || '';
    this.state.channelType = props.instagramChannel ? 'instagram' : channelType;
  }

  onSubmit = () => {
    const { channel, channelType, countries, email, gender } = this.state;
    const fullChannelUrl = getFullChannelUrl(channelType, channel);

    this.props.onSubmit({
      email,
      gender,
      channels: [
        {
          type: channelType,
          url: fullChannelUrl
        }
      ],
      locations: countries
        .filter(item => item.isSelected)
        .map(item => snakeCaseKeys({
          country_code: item.identifier,
          country: item.label
        }))
    })
      .then(influencerProfile => {
        if (influencerProfile && !influencerProfile.isProfileNew) {
          this.setState({
            existingProfileId: influencerProfile.id
          });
        }
      });
  };

  onValidationErrors = () => {
    this.setState({
      isValid: false,
      isModified: true
    });
  };

  handleTextInputChange = (partialState) => {
    this.setState({
      ...partialState,
      isValid: true,
      isModified: true,
      existingProfileId: null
    });
  };

  handleEmailInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.handleTextInputChange({ email: event.target.value });
  };

  handleChannelInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.handleTextInputChange({ channel: event.target.value });
  };

  handleCountriesChange = (countries: Array<MixedSelectDropdownItem>) => {
    this.setState({
      countries,
      isModified: true
    });
  };

  handleGenderChange = (gender: string) => {
    this.setState({
      gender,
      isValid: true,
      isModified: true
    });
  };

  handleChangeChannelType = (event: SyntheticEvent<TextEntry>) => {
    const value = event.currentTarget.value;

    this.setState({
      channelType: value,
      channel: '',
      existingProfileId: null
    });
  };

  getGenderOptions() {
    return GENDER_OPTIONS.map(gender => ({
      value: gender,
      name: __t(`${COMMON_MSG_KEY}.gender.${gender.toLowerCase()}`)
    }));
  }

  renderChannelInput() {
    const { channel, channelType, existingProfileId } = this.state;
    const { errorMessage } = this.props;
    const fullChannelUrl = getFullChannelUrl(channelType, channel);

    return (
      <FormField
        key={channelType}
        showRequiredMark
        label={__t(`lightweight_profile_form.form.influencer_${channelType}_channel`)}
        labelType="highlighted"
        fontSize="small"
        direction="column"
        showValidationIcon={false}
        validators={[
          isRequired(channel),
          isChannelUrl(channelType, fullChannelUrl, __t(`lightweight_profile_form.form.influencer_${channelType}_channel_example`))
        ]}
        immediateValidationError={errorMessage || ''}
      >
        <Input
          name="channel"
          width="responsive"
          value={channel}
          className={`lightweight-profile-form__channel--${channelType}`}
          hint={INFLUENCER_CHANNELS[channelType].short}
          hintClassName="lightweight-profile-form__channel-hint"
          placeholder={__t(`lightweight_profile_form.form.influencer_${channelType}_channel_placeholder`)}
          onChange={this.handleChannelInputChange}
        />
        {
          existingProfileId &&
            <div className="lightweight-profile-form__info-note">
              <VerticalSpacing type="small" />
              <InfoNote>
                {`${__t('lightweight_profile_form.form.info_note')} `}
                <ActionLink
                  size="inherit"
                  weight="bold"
                  openInNewTab
                  link={`/content-creator/profiles/${existingProfileId}`}
                >
                  {__t('lightweight_profile_form.form.link_view_profile')}
                </ActionLink>
              </InfoNote>
            </div>
        }
      </FormField>
    );
  }

  render(): React.ReactNode {
    const { channelType, countries, email, gender, existingProfileId, isValid, isModified } = this.state;
    const { onCancel, instagramChannel } = this.props;

    return (
      <section className="lightweight-profile-form">
        {
          isModified &&
            <PromptExit type="modal" />
        }
        <ModalFrameInner
          narrowContent
        >
          <Form
            onSubmit={this.onSubmit}
            onValidationErrors={this.onValidationErrors}
          >
            <InfoNote>
              {__t('lightweight_profile_form.form.no_brand_channels')}
            </InfoNote>
            <VerticalSpacing type="medium" />
            {
              !instagramChannel &&
              <FormField
                label={__t('lightweight_profile_form.form.channel')}
                direction="column"
                fontSize="small"
                labelType="highlighted"
                showRequiredMark={false}
                showValidationIcon={false}
              >
                {
                  ['instagram', 'youtube', 'tiktok'].map(option => (
                    <InputRadio
                      id={`lightweight-profile-form-${option}`}
                      key={`lightweight-profile-form-${option}`}
                      name="channelType"
                      label={__t(`lightweight_profile_form.form.channel_type.${option}`)}
                      value={option}
                      checked={channelType === option}
                      onChange={this.handleChangeChannelType}
                    />
                  ))
                }
              </FormField>
          }
            {!instagramChannel && this.renderChannelInput()}
            <FormField
              label={__t('lightweight_profile_form.form.influencer_gender')}
              labelType="highlighted"
              fontSize="small"
              direction="column"
              showValidationIcon={false}
            >
              <RadioButtons
                direction="row"
                selected={gender}
                options={this.getGenderOptions()}
                onChange={this.handleGenderChange}
              />
            </FormField>
            <FormField
              key="address-settings-form__countries"
              label={__t('lightweight_profile_form.form.influencer_countries')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showValidationIcon={false}
            >
              <SearchableDropdown
                items={countries}
                maxItems={5}
                dataFetcher={countrySearchToDropdownItems}
                onItemsUpdate={this.handleCountriesChange}
              />
            </FormField>
            <FormField
              label={__t('lightweight_profile_form.form.influencer_email')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showValidationIcon={false}
            >
              <Input
                name="email"
                width="responsive"
                value={email}
                onChange={this.handleEmailInputChange}
              />
            </FormField>
            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCancel}
              >
                {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
              </ActionButton>
              <ActionButton
                type="submit"
                disabled={!isValid || Boolean(existingProfileId)}
              >
                {__t(`${COMMON_MSG_KEY}.action_button.save`)}
              </ActionButton>
            </ModalFrameFooter>
          </Form>
        </ModalFrameInner>
      </section>
    );
  }
}

const mapStateToProps = ({ lightweightProfile, discoveryCreators }): StateProps => ({
  filters: discoveryCreators.filters,
  errorMessage: lightweightProfile.errorMessage
});

export default connect(mapStateToProps)(LightweightProfileForm);
